import { useEffect, useState } from "react";
import EntityManager from "@/components/EntityManager";
import { VehicleVaultDialog } from "@/components/VehicleVaultDialog";
import { api, API, formatApiError } from "@/lib/api";
import { fmtDate, today } from "@/lib/format";
import { Paperclip, Trash2 } from "lucide-react";
import { BRANCHES } from "@/lib/branches";
import { useAuth } from "@/context/AuthContext";
import { printVehicleMaster } from "@/lib/print";
import { toast } from "sonner";

const isPast = (d) => d && /^\d{4}-\d{2}-\d{2}$/.test(String(d).slice(0, 10)) && String(d).slice(0, 10) < today();

const isBlack = (r) => String(r?.blacklisted || "").toLowerCase() === "yes" || r?.blacklisted === true;

const exportVehiclesCsv = (list) => {
  const headers = ["Sl No.", "Vehicle No.", "Vehicle Type", "Make Year", "Model", "Model Type", "Chassis No.", "Engine No.", "Owner Name", "Owner Mobile", "Owner PAN", "Insurance Company", "Policy No.", "Insurance Valid Upto", "Permit Valid Upto", "FC Valid Upto", "Driver Name", "Driver Mobile", "DL No.", "DL Validity", "Blacklisted", "Blacklist Reason", "Notes"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.vehicle_no || "", r.vehicle_type || "", r.make || "", r.model || "", r.model_type || "", r.chassis_no || "", r.engine_no || "", r.owner_name || "", r.owner_mobile || "", r.pan_no || "", r.insurance_co || "", r.policy_no || "", r.ins_valid_upto || "", r.permit_valid_upto || "", r.fc_valid_upto || "", r.driver_name || "", r.driver_phone || "", r.dl_no || "", r.dl_validity || "", isBlack(r) ? "YES" : "No", r.blacklist_reason || "", r.notes || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-vehicle-master-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const DateCell = ({ v }) => {
  if (!v) return <span className="text-muted-foreground">—</span>;
  return <span className={isPast(v) ? "font-bold text-red-600" : ""}>{fmtDate(String(v).slice(0, 10))}{isPast(v) ? " ⚠" : ""}</span>;
};

export default function VehicleMaster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vaultVeh, setVaultVeh] = useState(null);
  const [vaultDocsAll, setVaultDocsAll] = useState([]);
  const [newDocs, setNewDocs] = useState({});
  const [sumKey, setSumKey] = useState(0);
  useEffect(() => {
    api.get("/vehicle-docs-all").then((r) => setVaultDocsAll(r.data || [])).catch(() => {});
  }, [sumKey]);
  const docsByVeh = {};
  vaultDocsAll.forEach((d) => { (docsByVeh[d.vehicle_key] = docsByVeh[d.vehicle_key] || []).push(d); });
  return (
    <>
    <EntityManager
      title="Vehicle Master"
      subtitle="Enter each market vehicle once — its details auto-fill on the Vehicle Hire Challan when you type the vehicle number."
      endpoint="vehicle-master"
      entityName="Vehicle"
      windowControls
      selectable
      onPrintSelected={printVehicleMaster}
      headerExtra={(rows, shown2, fvals, load2, setFvals) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {isAdmin ? (
            BRANCHES.map((b) => {
              const n = (rows || []).filter((r) => (r.branch || "") === b).length;
              const active = fvals?.branch === b;
              return n ? (
                <button
                  type="button"
                  key={b}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                  title={active ? "Tap to show all branches again" : `Tap to show only ${b}'s vehicles`}
                  onClick={() => setFvals((s) => ({ ...s, branch: active ? "All Branches" : b }))}
                  data-testid={`vm-branch-count-${b.toLowerCase()}`}
                >
                  {b} <b className={active ? "" : "text-primary"}>{n}</b>
                </button>
              ) : null;
            })
          ) : (
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700" data-testid="vm-branch-lock-pill">
              Showing your branch: {user?.office || "—"}
            </span>
          )}
        </div>
      )}
      filters={[
        isAdmin
          ? {
              key: "branch",
              label: "Branch",
              type: "select",
              options: ["All Branches", ...BRANCHES],
              match: (r, v) => !v || v === "All Branches" || (r.branch || "") === v,
            }
          : {
              key: "branch",
              hidden: true,
              match: (r) => !r.branch || (r.branch || "") === (user?.office || ""),
            },
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Vehicle no / owner / driver / type",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.vehicle_no, r.owner_name, r.owner_mobile, r.driver_name, r.driver_phone, r.vehicle_type, r.model, r.insurance_co]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
      ]}
      exportCsv={exportVehiclesCsv}
      defaults={{
        vehicle_no: "",
        branch: BRANCHES.includes(user?.office) ? user.office : "",
        vehicle_type: "",
        make: "",
        model: "",
        model_type: "",
        chassis_no: "",
        engine_no: "",
        owner_name: "",
        owner_mobile: "",
        pan_no: "",
        insurance_co: "",
        policy_no: "",
        ins_valid_upto: "",
        permit_valid_upto: "",
        fc_valid_upto: "",
        driver_name: "",
        driver_phone: "",
        dl_no: "",
        dl_validity: "",
        blacklisted: "No",
        blacklist_reason: "",
        notes: "",
      }}
      fields={[
        { key: "vehicle_no", label: "Vehicle No.", type: "text", placeholder: "RJ14 GA 4321", required: true },
        { key: "branch", label: "Branch", type: "lookup", placeholder: "Select branch", options: BRANCHES.map((b) => ({ value: b, label: b })) },
        { key: "vehicle_type", label: "Vehicle Type", type: "text", placeholder: "32 FT SXL" },
        { key: "make", label: "Vehicle Make Year", type: "text",
          autoFill: async (v) => {
            const y = parseInt(String(v || "").match(/(19|20)\d{2}/)?.[0] || "", 10);
            if (y) {
              const age = new Date().getFullYear() - y;
              if (age > 15) {
                toast.warning(`⚠ OLD VEHICLE — make year ${y} is ${age} years old`, {
                  description: "Many routes restrict vehicles older than 15 years.",
                  duration: 10000,
                });
              }
            }
            return null;
          } },
        { key: "model", label: "Vehicle Model", type: "text" },
        { key: "model_type", label: "Vehicle Model Type", type: "text" },
        { key: "chassis_no", label: "Chassis No.", type: "text" },
        { key: "engine_no", label: "Engine No.", type: "text" },
        { key: "owner_name", label: "Vehicle Owner Name", type: "text" },
        { key: "owner_mobile", label: "Vehicle Owner Mobile No.", type: "text" },
        { key: "pan_no", label: "Vehicle Owner PAN No.", type: "text" },
        { key: "insurance_co", label: "Insurance Company Name", type: "text" },
        { key: "policy_no", label: "Insurance Policy No.", type: "text" },
        { key: "ins_valid_upto", label: "Insurance Valid Upto", type: "date" },
        { key: "permit_valid_upto", label: "Permit Valid Upto", type: "date" },
        { key: "fc_valid_upto", label: "FC (Fitness Certificate) Valid Upto", type: "date" },
        { key: "driver_name", label: "Driver Name", type: "text" },
        { key: "driver_phone", label: "Driver Mobile No.", type: "text" },
        { key: "dl_no", label: "Driving License No.", type: "text" },
        { key: "dl_validity", label: "Driving License Validity", type: "date" },
        { key: "blacklisted", label: "Blacklisted?", type: "select", options: ["No", "Yes"] },
        { key: "blacklist_reason", label: "Blacklist Reason", type: "text", placeholder: "Why is this vehicle blocked?" },
        { key: "notes", label: "Notes", type: "text" },
      ]}
      formExtra={() => (
        <div className="rounded-md border border-dashed border-border p-3 sm:col-span-2" data-testid="vm-attachments-section">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Attachments — uploaded to the vault on save</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {["RC", "PUC", "Insurance", "Permit"].map((dt) => (
              <div key={dt} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${newDocs[dt] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-border"}`} data-testid={`vm-new-row-${dt.toLowerCase()}`}>
                <span className="font-bold">{dt}</span>
                {newDocs[dt] && /^image\//.test(newDocs[dt].type || "") ? (
                  <img src={URL.createObjectURL(newDocs[dt])} alt={dt} className="h-8 w-8 shrink-0 rounded border object-cover" data-testid={`vm-new-thumb-${dt.toLowerCase()}`} />
                ) : null}
                <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground">{newDocs[dt]?.name || "Not attached"}</span>
                {newDocs[dt] ? (
                  <>
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-sky-600 hover:underline"
                      onClick={() => window.open(URL.createObjectURL(newDocs[dt]), "_blank")}
                      data-testid={`vm-new-view-${dt.toLowerCase()}`}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700"
                      title="Remove this file"
                      onClick={() => setNewDocs((p) => { const n = { ...p }; delete n[dt]; return n; })}
                      data-testid={`vm-new-remove-${dt.toLowerCase()}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : null}
                <label className="cursor-pointer rounded border border-border bg-card px-2 py-1 text-[10px] font-semibold hover:bg-accent" data-testid={`vm-new-attach-${dt.toLowerCase()}`}>
                  {newDocs[dt] ? "Change" : "Attach"}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setNewDocs((p) => ({ ...p, [dt]: f }));
                      e.target.value = "";
                    }}
                    data-testid={`vm-new-file-${dt.toLowerCase()}`}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      onSaved={(saved) => {
        const staged = Object.entries(newDocs).filter(([, f]) => f);
        if (!staged.length || !saved?.vehicle_no) return;
        (async () => {
          let ok = 0;
          for (const [dt, f] of staged) {
            const fd = new FormData();
            fd.append("doc_type", dt);
            fd.append("file", f);
            try {
              await api.post(`/vehicle-docs/${encodeURIComponent(saved.vehicle_no)}`, fd);
              ok += 1;
            } catch (e) {
              toast.error(`${dt}: ${formatApiError(e.response?.data?.detail)}`);
            }
          }
          setNewDocs({});
          setSumKey((k) => k + 1);
          if (ok) toast.success(`${ok} document(s) attached to the vault of ${saved.vehicle_no}`);
        })();
      }}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, i) => (typeof i === "number" ? i + 1 : "—") },
        { key: "branch", label: "Branch", render: (r) => r.branch || "—" },
        { key: "vehicle_no", label: "Vehicle No.", render: (r) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="font-bold">{r.vehicle_no}</span>
            {isBlack(r) ? (
              <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white" title={r.blacklist_reason || "Blacklisted vehicle"} data-testid="master-blacklist-chip">BLACKLISTED</span>
            ) : null}
          </span>
        ) },
        { key: "vehicle_type", label: "Type" },
        { key: "make", label: "Make Year" },
        { key: "owner_name", label: "Owner" },
        { key: "owner_mobile", label: "Owner Mobile" },
        { key: "ins_valid_upto", label: "Insurance Upto", render: (r) => <DateCell v={r.ins_valid_upto} /> },
        { key: "permit_valid_upto", label: "Permit Upto", render: (r) => <DateCell v={r.permit_valid_upto} /> },
        { key: "fc_valid_upto", label: "FC Upto", render: (r) => <DateCell v={r.fc_valid_upto} /> },
        { key: "driver_name", label: "Driver" },
        { key: "dl_validity", label: "DL Validity", render: (r) => <DateCell v={r.dl_validity} /> },
        { key: "_attached", label: "Attached", render: (r) => {
          const k = String(r.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
          const docs = ["RC", "Insurance", "Permit", "FC", "DL", "PUC"].map((t) => (docsByVeh[k] || []).find((d) => d.doc_type === t)).filter(Boolean);
          if (!docs.length) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className="inline-flex flex-wrap gap-1">
              {docs.map((d) => (
                <button
                  key={d.doc_type}
                  type="button"
                  className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-100"
                  title={`View ${d.doc_type} copy`}
                  onClick={(e) => { e.stopPropagation(); window.open(`${API}/files/${d.path}?auth=${localStorage.getItem("ntc_token")}`, "_blank"); }}
                  data-testid={`master-doc-${d.doc_type.toLowerCase()}`}
                >
                  {d.doc_type}
                </button>
              ))}
            </span>
          );
        } },
        { key: "_vault", label: "Vault", render: (r) => {
          const k = String(r.vehicle_no || "").replace(/\s+/g, "").toUpperCase();
          const types = (docsByVeh[k] || []).map((d) => d.doc_type);
          return (
            <button
              type="button"
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${types.length ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              title={types.length ? `Attached: ${types.join(", ")} — tap to open the Vehicle Document Vault` : "No documents attached — tap to add RC, Insurance, Permit, FC, DL & PUC copies"}
              onClick={(e) => { e.stopPropagation(); setVaultVeh(r.vehicle_no); }}
              data-testid="master-vault-btn"
            >
              <Paperclip className="h-3 w-3" /> {types.length ? `${types.length}/6` : "Add"}
            </button>
          );
        } },
      ]}
    />
    {vaultVeh ? (
      <VehicleVaultDialog vehicleNo={vaultVeh} onClose={() => setVaultVeh(null)} onChanged={() => setSumKey((k) => k + 1)} />
    ) : null}
    </>
  );
}
